import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, DanyComposer, FixyBeeMark, SuggestionChips } from "@/ds";
import { useDanyUi } from "@/components/dany/DanyUiContext";
import { useSmartCTA } from "@/hooks/useSmartCTA";
import { MODE_GENERAL, MODE_PROVIDER } from "@/lib/dany";

/** How long each placeholder intention holds before the bar offers the other. */
const PLACEHOLDER_MS = 4500;

/**
 * The front door: Dany's hero, and the only hero on fixydany.com.
 *
 * `fixydany.com` opens with Dany as the absolute protagonist and the landing
 * lives below the scroll (DANY.md §2.5). The marketing kit's own hero — "Get it
 * fixed today by a FixyBee" and its second search bar — is discarded whole:
 * two heroes and two search bars on one page is the bug, not the feature.
 *
 * Dual audience, per §2.5:
 *   1. The bar itself is agnostic and the placeholder rotates between a client
 *      intention and a provider one.
 *   2. Two chip groups with the same visual weight — client first (§2.5 point 6
 *      gives the client reading priority), provider second — plus the Pro
 *      banner as the direct route to `/become-a-fixybee`.
 *   3. Picking a provider chip opens the thread in provider mode. The mode is
 *      only a hint: `DanyContext.resolved_mode()` lets a signed-in account's
 *      real role win.
 *
 * Submitting hands the first message to `/dany` through router state. The hero
 * never renders a thread of its own — one conversational surface, two doors.
 */
export default function DanyHomeHero() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cta = useSmartCTA();
  const { registerConversationBox } = useDanyUi();

  const [value, setValue] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [placeholderIndex, setPlaceholderIndex] = React.useState(0);
  const [heroError, setHeroError] = React.useState(null);

  const placeholders = React.useMemo(() => {
    const list = t("dany.home.placeholders", { returnObjects: true });
    return Array.isArray(list) && list.length ? list : [t("dany.home.placeholder")];
  }, [t]);

  // Rotation pauses the moment there is text: a placeholder that keeps changing
  // under a half-typed sentence is noise, and it is invisible anyway.
  React.useEffect(() => {
    if (value.trim()) return undefined;
    const timer = setInterval(
      () => setPlaceholderIndex((i) => (i + 1) % placeholders.length),
      PLACEHOLDER_MS
    );
    return () => clearInterval(timer);
  }, [value, placeholders.length]);

  const ask = (text, mode = MODE_GENERAL) => {
    const message = String(text ?? "").trim();
    if (!message) return;
    // The ZIP pill has to reach the model somehow: `find_fixybees` ranks by
    // distance and POST /dany/chat carries no location field (DY1.3 is closed).
    // Appending it to the message keeps it visible in the client's own turn —
    // they can see exactly what Dany was told — instead of smuggling it in a
    // hidden field.
    const clean = zip.trim();
    const seed = /^\d{5}$/.test(clean) ? t("dany.home.withZip", { message, zip: clean }) : message;
    navigate("/dany", { state: { seed, mode } });
  };

  /**
   * The microphone's half of `ask`.
   *
   * It exists as a named function for the reason the bug that produced it
   * demands: the handler used to be an inline closure in the JSX that read a
   * bare `mode`, and `mode` is a PARAMETER OF `ask` — not a binding of this
   * component. Nothing failed at render, because an arrow body is not evaluated
   * until it is called; the button drew itself perfectly and threw
   * `ReferenceError: mode is not defined` the moment anyone pressed it.
   * Verified in production before this fix, incognito: the route never changed
   * and no request was made.
   *
   * The mode is `MODE_GENERAL` and is written out rather than defaulted: the
   * microphone sits on the agnostic bar, above both chip groups, so it commits
   * to neither audience — the same value `ask` falls back to when a chip did
   * not choose one.
   *
   * The try/catch is not defensive noise. A throw inside a React event handler
   * goes to the console and nowhere else, which is precisely how this defect
   * reached a user: a button that looked alive and did nothing, with the reason
   * visible only to someone with devtools open. The standing rule is that
   * nothing fails silently, and a handler is exactly where that is easiest to
   * violate by accident.
   */
  const startVoiceIntent = () => {
    setHeroError(null);
    try {
      navigate("/dany", { state: { startVoice: true, mode: MODE_GENERAL } });
    } catch (err) {
      setHeroError(String(err?.message || err));
    }
  };

  // A missing key comes back as the key itself, not undefined, so `|| []` would
  // not catch it — and SuggestionChips would try to map over a string.
  const asChips = (key) => {
    const list = t(key, { returnObjects: true });
    return Array.isArray(list) ? list : [];
  };
  const clientChips = asChips("dany.home.suggestions");
  const providerChips = asChips("dany.home.providerSuggestions");

  return (
    <section
      className="bg-honeycomb"
      data-testid="dany-home-hero"
      style={{
        minHeight: "calc(100svh - var(--navbar-height))",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "var(--section-padding-y) var(--section-padding-x)",
      }}
    >
      <span style={{ position: "relative", display: "inline-flex", marginBottom: 24 }}>
        <span style={{ position: "absolute", inset: -28, background: "var(--dany-halo)", borderRadius: "50%" }} />
        <FixyBeeMark size={92} animated style={{ position: "relative", maxWidth: "28vw", height: "auto" }} />
      </span>

      <h1 style={{
        fontFamily: "var(--font-display)", fontWeight: 800,
        fontSize: "var(--text-hero)", lineHeight: "var(--leading-hero)",
        letterSpacing: "var(--tracking-tight)", textAlign: "center",
        maxWidth: "var(--measure-hero)", textWrap: "balance",
      }}>
        {t("dany.home.greeting")}
      </h1>

      <p style={{
        marginTop: "var(--space-4)", marginBottom: "var(--space-8)", textAlign: "center",
        fontFamily: "var(--font-body)", fontSize: "var(--text-lede-fluid)",
        lineHeight: "var(--leading-relaxed)", color: "var(--text-muted)", maxWidth: "52ch",
      }}>
        {t("dany.home.sub")}
      </p>

      {/* The FAB's trigger. Wrapped rather than ref-forwarded through
          DanyComposer: what has to be observed is the box as it sits on the
          page, and a wrapper measures exactly that without giving a design
          system component a ref it has no other reason to expose. */}
      <div
        ref={registerConversationBox}
        data-testid="dany-hero-box"
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        {/* Voice is open to anonymous visitors since 2026-08-17 (Daniel), so the
            hero's mic stops being decoration. It does not open a call here: it
            hands the intent to the thread surface, which already owns every
            state a call has — the avatar, the live tool cards, the transcript.
            Duplicating that in a landing hero would be a second implementation
            to keep in step with the first. */}
        <DanyComposer
          size="hero"
          value={value}
          onChange={setValue}
          onSubmit={() => ask(value)}
          zip={zip}
          onZipChange={setZip}
          zipPlaceholder={t("dany.home.zipPlaceholder")}
          zipLabel={t("dany.home.zipLabel")}
          placeholder={placeholders[placeholderIndex % placeholders.length]}
          // Below 768px the field is ~176px wide and the rotating lines do not
          // fit. The provider intention the rotation was carrying is right below
          // as its own chip group, so a phone loses nothing by pinning one short
          // line — and a placeholder that changes under a cramped field is noise.
          placeholderShort={t("dany.home.placeholderShort")}
          footnote={t("dany.home.footnote")}
          attachments={false}
          voiceTitle={t("dany.voice.start")}
          onVoice={startVoiceIntent}
        />
      </div>

      {heroError && (
        <div style={{ marginTop: "var(--space-4)", maxWidth: 760, width: "100%" }}>
          <Alert tone="danger" title={t("dany.voice.errorTitle")}>
            {t("dany.voice.voice_error")}
          </Alert>
        </div>
      )}

      {/* Dual chips, equal weight. Client first — §2.5 point 6. */}
      <div style={{ marginTop: 36, maxWidth: 760, width: "100%", display: "grid", gap: "var(--space-5)" }}>
        <SuggestionChips
          label={t("dany.home.tryAsking")}
          items={clientChips}
          onPick={(s) => ask(s.label)}
        />
        <SuggestionChips
          label={t("dany.home.forProviders")}
          items={providerChips}
          onPick={(s) => ask(s.label, MODE_PROVIDER)}
        />
      </div>

      <div style={{ marginTop: "var(--space-10)", maxWidth: 760, width: "100%" }}>
        <Card variant="inverse" padding="lg" style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{
              fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "var(--text-2xs)",
              letterSpacing: "var(--tracking-widest)", textTransform: "uppercase", color: "var(--hive)",
            }}>
              {t("dany.home.forBees")}
            </p>
            <p style={{
              marginTop: 8, fontFamily: "var(--font-body)", fontSize: "var(--text-sm)",
              lineHeight: "var(--leading-relaxed)", color: "var(--cream-a80)", maxWidth: "var(--measure-lede)",
            }}>
              {t("dany.home.forBeesBody")}
            </p>
          </div>
          {/* Secondary (REG-A): the landing's one primary is "I need a
              service", further down. */}
          <Button as={Link} to={cta.becomeFixyBee()} variant="secondary" iconRight="arrow-right"
                  data-surface="inverse" data-testid="hero-become-bee">
            {t("dany.home.forBeesCta")}
          </Button>
        </Card>
      </div>
    </section>
  );
}
